import type { ApiPort } from '@flowx/module-contracts'

export interface ApiResponse<T = unknown> {
  code: number
  message: string
  data?: T
}

export interface Agent {
  code: string
  name: string
  description: string
  avatar_url: string
  category: string
  use_count: number
}

export interface Conversation {
  uuid: string
  title: string
  agent_code: string
  agent_name: string
  message_count: number
  last_message: string
  last_message_at: string
  created_at: string
}

export interface Message {
  id: number
  role: 'user' | 'assistant' | 'system'
  content: string
  content_type: string
  agent_code: string
  token_count: number
  created_at: string
}

export interface Workflow {
  code: string
  name: string
  description: string
  cover_url: string
  category: string
  use_count: number
}

export interface ChatResponse {
  conversation_id: string
  message: Message
  token_count: number
  duration_ms: number
}

const queryPath = (path: string, params?: Record<string, string | number | undefined>) => {
  const query = new URLSearchParams()
  for (const [key, value] of Object.entries(params ?? {})) {
    if (value !== undefined) query.set(key, String(value))
  }
  const encoded = query.toString()
  return encoded.length > 0 ? `${path}?${encoded}` : path
}

export const createXiaowanApi = (api: ApiPort) => ({
  getAgents: (category?: string, keyword?: string) =>
    api.request<ApiResponse<{ items: Agent[] }>>({
      method: 'GET',
      path: queryPath('/xiaowan/agents', { category, keyword }),
    }),
  getAgent: (code: string) =>
    api.request<ApiResponse<{ agent: Agent; example_prompts: string[] }>>({
      method: 'GET',
      path: `/xiaowan/agents/${code}`,
    }),
  getWorkflows: (params?: { category?: string; keyword?: string; page?: number; page_size?: number }) =>
    api.request<ApiResponse<{ items: Workflow[]; page: number; page_size: number; total: number }>>({
      method: 'GET',
      path: queryPath('/xiaowan/workflows', params),
    }),
  chat: (body: { conversation_id?: string; agent_code: string; content: string; content_type?: string; file_urls?: string[] }) =>
    api.request<ApiResponse<ChatResponse>>({ method: 'POST', path: '/xiaowan/chat', body }),
  getConversations: (params?: { agent_code?: string; status?: number; page?: number; page_size?: number }) =>
    api.request<ApiResponse<{ items: Conversation[]; page: number; page_size: number; total: number }>>({
      method: 'GET',
      path: queryPath('/xiaowan/conversations', params),
    }),
  getConversation: (uuid: string) =>
    api.request<ApiResponse<{ conversation: Conversation; agent: Agent }>>({
      method: 'GET',
      path: `/xiaowan/conversations/${uuid}`,
    }),
  deleteConversation: (uuid: string) =>
    api.request<ApiResponse<null>>({ method: 'DELETE', path: `/xiaowan/conversations/${uuid}` }),
  getMessages: (conversationUuid: string, params?: { page?: number; page_size?: number }) =>
    api.request<ApiResponse<{ items: Message[]; page: number; page_size: number; total: number }>>({
      method: 'GET',
      path: queryPath(`/xiaowan/conversations/${conversationUuid}/messages`, params),
    }),
})
